import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Users, Plus, Download, Check, X } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import EventFormDialog from '@/components/groups/EventFormDialog';
import CalendarView from '@/components/groups/CalendarView';
import { downloadICSFile } from '@/utils/icsDownload';

export default function CalendarioGrupo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('groupId');
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'

  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => base44.auth.me()
  });

  const { data: member } = useQuery({
    queryKey: ['member', user?.email],
    queryFn: () => base44.entities.Member.filter({ user_id: user.id, status: 'Ativo' }).then(m => m[0]),
    enabled: !!user
  });

  const { data: group, isLoading: loadingGroup } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => base44.entities.Group.filter({ id: groupId }).then(g => g[0]),
    enabled: !!groupId
  });

  const { data: events = [], isLoading: loadingEvents } = useQuery({
    queryKey: ['group-events', groupId],
    queryFn: () => base44.entities.GroupEvent.filter({ group_id: groupId }, 'date'),
    enabled: !!groupId
  });

  const updateAttendanceMutation = useMutation({
    mutationFn: ({ eventId, status }) => {
      const event = events.find(e => e.id === eventId);
      const updatedAttendees = event.attendees?.map(a => 
        a.member_id === member.id ? { ...a, status } : a
      ) || [];
      
      return base44.entities.GroupEvent.update(eventId, { attendees: updatedAttendees });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-events'] });
      toast.success('Resposta atualizada!');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.GroupEvent.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-events'] });
      toast.success('Evento excluído!');
    }
  });

  const exportToCalendar = async (event) => {
    const startDateTime = moment(`${event.date} ${event.start_time}`).format('YYYYMMDDTHHmmss');
    const endDateTime = moment(`${event.date} ${event.end_time || event.start_time}`).add(1, 'hour').format('YYYYMMDDTHHmmss');
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Church App//Group Event//EN',
      'BEGIN:VEVENT',
      `UID:${event.id}@churchapp`,
      `DTSTAMP:${moment().format('YYYYMMDDTHHmmss')}`,
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description || ''}`,
      `LOCATION:${event.location || ''}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    await downloadICSFile(icsContent, `${event.title.replace(/\s/g, '_')}.ics`);
  };

  const isLeader = member && group?.leaders?.some(l => l.leader_id === member.id);
  const isMember = member && group?.member_ids?.includes(member.id);

  if (!groupId) {
    navigate('/Grupos');
    return null;
  }

  if (loadingGroup || loadingEvents || !member) {
    return <LoadingSpinner text="Carregando calendário..." />;
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Grupo não encontrado</p>
      </div>
    );
  }

  if (!isMember && !isLeader) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
          <p className="text-gray-600">Você precisa ser membro deste grupo para ver o calendário.</p>
        </div>
      </div>
    );
  }

  const upcomingEvents = events.filter(e => moment(e.date).isSameOrAfter(moment(), 'day'));
  const pastEvents = events.filter(e => moment(e.date).isBefore(moment(), 'day'));

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <PageHeader 
            title={`Calendário - ${group.name}`}
            subtitle={isLeader ? 'Gerencie os eventos do grupo' : 'Confira os próximos eventos'}
            action={
              isLeader && (
                <Button 
                  onClick={() => setShowEventForm(true)}
                  className="bg-white text-indigo-600 hover:bg-gray-100"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Evento
                </Button>
              )
            }
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-14">
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              onClick={() => setViewMode('list')}
              className="flex-1"
            >
              Lista
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'outline'}
              onClick={() => setViewMode('calendar')}
              className="flex-1"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Calendário
            </Button>
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <CalendarView events={events} onEventClick={(event) => {
            if (isLeader) {
              setEditingEvent(event);
              setShowEventForm(true);
            }
          }} />
        ) : (
          <>
            {upcomingEvents.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Próximos Eventos</h2>
                <div className="space-y-4">
                  {upcomingEvents.map((event) => {
                    const userAttendance = event.attendees?.find(a => a.member_id === member.id);
                    
                    return (
                      <div key={event.id} className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">{event.title}</h3>
                            <div className="space-y-2 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>{moment(event.date).format('DD/MM/YYYY')}</span>
                              </div>
                              {event.start_time && (
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  <span>{event.start_time}{event.end_time && ` - ${event.end_time}`}</span>
                                </div>
                              )}
                              {event.location && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  <span>{event.location}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {isLeader && (
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingEvent(event);
                                  setShowEventForm(true);
                                }}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteMutation.mutate(event.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Excluir
                              </Button>
                            </div>
                          )}
                        </div>

                        {event.description && (
                          <p className="text-gray-600 text-sm mb-4">{event.description}</p>
                        )}

                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => exportToCalendar(event)}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Exportar
                          </Button>

                          {isMember && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={userAttendance?.status === 'confirmed' ? 'default' : 'outline'}
                                onClick={() => updateAttendanceMutation.mutate({ eventId: event.id, status: 'confirmed' })}
                                className={userAttendance?.status === 'confirmed' ? 'bg-green-600 hover:bg-green-700' : ''}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Confirmar
                              </Button>
                              <Button
                                size="sm"
                                variant={userAttendance?.status === 'declined' ? 'default' : 'outline'}
                                onClick={() => updateAttendanceMutation.mutate({ eventId: event.id, status: 'declined' })}
                                className={userAttendance?.status === 'declined' ? 'bg-red-600 hover:bg-red-700' : ''}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Recusar
                              </Button>
                            </div>
                          )}
                        </div>

                        {event.attendees && event.attendees.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-sm font-medium text-gray-700 mb-2">
                              Confirmados: {event.attendees.filter(a => a.status === 'confirmed').length}/{event.attendees.length}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {pastEvents.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Eventos Passados</h2>
                <div className="space-y-4">
                  {pastEvents.map((event) => (
                    <div key={event.id} className="bg-gray-50 rounded-xl p-5 opacity-75">
                      <h3 className="text-lg font-semibold text-gray-700 mb-1">{event.title}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{moment(event.date).format('DD/MM/YYYY')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {events.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum evento agendado</h3>
                <p className="text-gray-600 mb-4">
                  {isLeader ? 'Crie o primeiro evento do grupo' : 'Aguarde novos eventos serem criados'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {isLeader && (
        <EventFormDialog
          open={showEventForm}
          onClose={() => {
            setShowEventForm(false);
            setEditingEvent(null);
          }}
          event={editingEvent}
          group={group}
          members={member ? [member] : []}
        />
      )}
    </div>
  );
}
